import type { Geometry } from './Slider';

/**
 * Everything around the slider on a phone/tablet lobby BUT the dock's
 * buttons: the header, the title + "What's inside" pill under the pack, the
 * fixed dock (Max Pull + the buy row's padding), plus a small breathing gap.
 * Measured off the sleek lobby.
 */
const LOBBY_CHROME_WITHOUT_BUTTONS_PX = 320;

/** The dock's stepper / Buy height when the operator hasn't set
 *  `--co-BottomActionShell-buttonHeight` (partner Figma 2026-10-14). */
const DEFAULT_DOCK_BUTTON_PX = 52;

const DEFAULT_LOBBY_CHROME_PX = LOBBY_CHROME_WITHOUT_BUTTONS_PX + DEFAULT_DOCK_BUTTON_PX;

/** Below this the pack would stop reading as the hero — scroll instead. */
const MIN_FIT = 0.6;

/** Narrowest sliver (px) of a peek pack a grown phone coverflow keeps on
 *  screen — the swipe hint. */
export const MOBILE_MIN_PEEK_VISIBLE_PX = 40;

/** Air (px) between the centre pack's edge and that peek sliver. */
const PEEK_GAP_PX = 12;

/**
 * How much to scale the slider so the whole lobby fills the screen: shrink
 * when it is too short to fit unscrolled (partner feedback 2026-10-07:
 * "since all the content fits, there shouldn't be an ability to scroll"),
 * grow up to `maxScale` when it is taller than the Figma frame (an iPhone
 * Pro Max left an empty band above the dock, feedback 2026-10-08).
 */
export function fitScale(
  availableHeight: number,
  sliderHeight: number,
  maxScale = 1,
  chromeHeight = DEFAULT_LOBBY_CHROME_PX,
): number {
  if (availableHeight <= 0 || sliderHeight <= 0) return 1;
  const room = (availableHeight - chromeHeight) / sliderHeight;
  return Math.min(maxScale, Math.max(MIN_FIT, room));
}

/**
 * Height (px) the lobby still has spare once the slider is scaled by `k` —
 * left over when a narrow screen caps the growth before the height runs out.
 */
export function spareHeight(
  availableHeight: number,
  sliderHeight: number,
  k: number,
  chromeHeight = DEFAULT_LOBBY_CHROME_PX,
): number {
  if (availableHeight <= 0) return 0;
  return Math.max(0, availableHeight - chromeHeight - sliderHeight * k);
}

/**
 * The most a phone pack may grow before it crowds the peeks: the centre
 * pack, a gap and a peek sliver on each side must fit the width. Never
 * below 1 — width alone doesn't shrink the Figma size.
 */
export function maxScaleForWidth(containerWidth: number, packWidth: number): number {
  const room = containerWidth - 2 * (MOBILE_MIN_PEEK_VISIBLE_PX + PEEK_GAP_PX);
  return Math.max(1, room / packWidth);
}

/** The geometry scaled as one piece, so the coverflow keeps its proportions. */
export function scaleGeometry(geometry: Geometry, k: number): Geometry {
  if (k === 1) return geometry;
  const px = (v: number) => Math.round(v * k);
  return {
    ...geometry,
    sliderHeight: px(geometry.sliderHeight),
    pack: { w: px(geometry.pack.w), h: px(geometry.pack.h), top: px(geometry.pack.top) },
    logo: geometry.logo
      ? { w: px(geometry.logo.w), h: px(geometry.logo.h), top: px(geometry.logo.top) }
      : null,
    pedestal: geometry.pedestal
      ? { w: px(geometry.pedestal.w), h: px(geometry.pedestal.h), top: px(geometry.pedestal.top) }
      : null,
    slotStep: px(geometry.slotStep),
  };
}

/** The geometry moved `dy` px down inside a slider that grows by as much. */
export function shiftGeometry(geometry: Geometry, dy: number): Geometry {
  if (dy === 0) return geometry;
  return {
    ...geometry,
    sliderHeight: geometry.sliderHeight + dy,
    pack: { ...geometry.pack, top: geometry.pack.top + dy },
    logo: geometry.logo ? { ...geometry.logo, top: geometry.logo.top + dy } : null,
    pedestal: geometry.pedestal ? { ...geometry.pedestal, top: geometry.pedestal.top + dy } : null,
  };
}

/**
 * How far (px) to pull the peeks in for pack art narrower than its box. The
 * box is sized for the stock pack; `object-contain` letterboxes thinner art
 * with empty bands at its sides, which push the peek art off the screen edge.
 * Taking out the peek's own (scaled) band shows as much of the peek's art as
 * the stock pack shows of its own. Taking out the centre pack's band as well
 * kept the stock gap instead, but then a thin peek showed most of itself and
 * the packs read as crowded (iPhone SE, 2026-10-21). Art as wide as the box or
 * wider (`artAspect` w/h), or not loaded yet (`null`), needs no pull.
 */
export function peekPullForArt(
  pack: Geometry['pack'],
  peekScale: number,
  artAspect: number | null,
): number {
  if (artAspect === null) return 0;
  const artWidth = Math.min(pack.w, pack.h * artAspect);
  const band = (pack.w - artWidth) / 2;
  return band * peekScale;
}

/**
 * Where `object-contain` puts pack art of `artAspect` (w/h) inside its box:
 * the empty band at the left and at the top, in px.
 */
export function artInset(pack: Geometry['pack'], artAspect: number): { x: number; y: number } {
  const artWidth = Math.min(pack.w, pack.h * artAspect);
  const artHeight = Math.min(pack.h, pack.w / artAspect);
  return { x: (pack.w - artWidth) / 2, y: (pack.h - artHeight) / 2 };
}

/** Where the pack's content ends — the pedestal when there is one, else the pack. */
export function contentBottom(geometry: Geometry): number {
  const lowest = geometry.pedestal ?? geometry.pack;
  return lowest.top + lowest.h;
}

/** The geometry with a pedestal under the pack: the slider grows by the band
 *  the pedestal adds below the pack, and the logo moves down past it. */
export function withPedestal(geometry: Geometry, pedestal: Geometry['pack']): Geometry {
  const band = pedestal.top + pedestal.h - (geometry.pack.top + geometry.pack.h);
  return {
    ...geometry,
    sliderHeight: geometry.sliderHeight + band,
    logo: geometry.logo ? { ...geometry.logo, top: geometry.logo.top + band } : null,
    pedestal,
  };
}

/** The screen height the lobby gets — the viewport minus the host insets
 *  `#root` pads with (status bar, home indicator). */
export function lobbyHeight(): number {
  const root = document.getElementById('root');
  const style = root ? getComputedStyle(root) : null;
  const insets = style ? (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0) : 0;
  return document.documentElement.clientHeight - insets;
}

/** The lobby chrome with the dock buttons at the height the operator gave
 *  them (`--co-BottomActionShell-buttonHeight`, read off `:root` where the
 *  theme writes its knobs) — the dock grows with them, the pack must not. */
export function lobbyChromeHeight(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(
    '--co-BottomActionShell-buttonHeight',
  );
  const buttons = parseFloat(raw);
  return LOBBY_CHROME_WITHOUT_BUTTONS_PX + (buttons > 0 ? buttons : DEFAULT_DOCK_BUTTON_PX);
}
